/**
 * continuityRecallSelector.js — FASE R5 selective recall gate.
 *
 * Decides WHETHER to surface the memory fragment before an LLM call.
 * Not every conversation warrants continuity recall.
 *
 * Recall SHOULD happen when:
 *   - Repeated grief, exhaustion, or unresolved conflict
 *   - Follow-up after crisis or care-intensity high/crisis
 *   - Long pastoral processes (3+ entries in journey history)
 *   - Entity has a milestone note that grounds the conversation
 *
 * Recall should NOT happen when:
 *   - Logistics / scheduling / simple factual requests
 *   - Planning (dates, locations, event prep)
 *   - No emotionally significant history
 *   - Conversation is the very first message with this entity
 *
 * Returns: { shouldRecall, fragment, reason }
 */

import { buildMemoryFragment } from "./memoryCompressionEngine";

// Intent keywords that indicate logistical / planning conversations
const LOGISTICS_PATTERNS = [
  /\bcuándo\b/i,
  /\bhorario\b/i,
  /\bagenda\b/i,
  /\bfecha\b/i,
  /\breunión\b/i,
  /\bvisita.*next\b/i,
  /\bpróxim[ao]\b/i,
  /\bprogramar\b/i,
  /\bplanificar\b/i,
  /\bcalendar\b/i,
  /\bdirección\b/i,
  /\bteléfono\b/i,
  /\bemail\b/i,
  /\bcorreo\b/i,
  /\bcuántas?\b/i,
  /\bcuántos?\b/i,
  /\bdónde\b/i,
  /\blistado\b/i,
  /\blista de\b/i,
];

// Intent keywords that indicate emotionally relevant conversations
const PASTORAL_PATTERNS = [
  /\bcómo está\b/i,
  /\bcómo se siente\b/i,
  /\bqué está pasando\b/i,
  /\bqué ha pasado\b/i,
  /\bseguimiento\b/i,
  /\brecuerdas?\b/i,
  /\bantes\b/i,
  /\bhablamos\b/i,
  /\bconversamos\b/i,
  /\bla última vez\b/i,
  /\bsigue\b/i,
  /\btodavía\b/i,
  /\baún\b/i,
  /\bcontinúa\b/i,
  /\bha mejorado\b/i,
  /\bsigue igual\b/i,
  /\bdifícil\b/i,
  /\bcansado\b/i,
  /\bpesado\b/i,
  /\bdolor\b/i,
  /\btristeza\b/i,
];

/**
 * Detect if the conversation is likely logistical (suppress memory).
 * @param {string} text  — user message
 * @returns {boolean}
 */
function isLogisticsQuery(text) {
  if (!text) return false;
  const matches = LOGISTICS_PATTERNS.filter((p) => p.test(text)).length;
  const pastoralMatches = PASTORAL_PATTERNS.filter((p) => p.test(text)).length;
  // Suppress only if logistics signals clearly dominate
  return matches >= 2 && pastoralMatches === 0;
}

// R7: Only recall for persistent/worsening patterns — not fragile/improving first-pass.
// Fragile recovery and one-time improvements are too light to surface unprompted.
const SIGNIFICANT_DIRECTIONS = new Set(["recurring", "stable_heavy", "worsening", "cycling"]);

/**
 * Determine if memory is strong enough to surface (signal threshold).
 * @param {{ fragment: string|null, hasMemory: boolean, anchor: string|null, direction: string }} memory
 * @returns {boolean}
 */
function isMemorySignificant(memory) {
  if (!memory?.hasMemory || !memory.fragment) return false;
  if (!memory.anchor) return false;
  // R7: Require direction to be in the significant set — suppress fragile/improving/unknown
  return SIGNIFICANT_DIRECTIONS.has(memory.direction);
}

/**
 * Selective recall gate — decides whether to inject the memory fragment.
 *
 * @param {{
 *   entityId:   string,
 *   entityType: string,
 * }|null} entity           — the resolved entity for this conversation (can be null)
 * @param {string}  userText     — the current user message
 * @param {boolean} hasHistory   — whether there are prior messages in this conversation
 * @returns {{
 *   shouldRecall: boolean,
 *   fragment:     string|null,
 *   reason:       string,
 * }}
 */
export function selectContinuityRecall(entity, userText = "", hasHistory = false) {
  const noRecall = { shouldRecall: false, fragment: null, reason: "no_recall" };

  // No entity context — nothing to recall
  if (!entity?.entityId) return noRecall;

  // Mid-conversation: recall is already implicit in message history
  if (hasHistory) return noRecall;

  // Suppress for logistics / planning
  if (isLogisticsQuery(userText)) {
    return { shouldRecall: false, fragment: null, reason: "logistics_suppressed" };
  }

  const memory = buildMemoryFragment(entity);

  if (!isMemorySignificant(memory)) {
    return { shouldRecall: false, fragment: null, reason: "no_significant_memory" };
  }

  return {
    shouldRecall: true,
    fragment:     memory.fragment,
    reason:       memory.direction !== "unknown" ? memory.direction : "anchor_recall",
  };
}
